import { Component, inject, Input, OnDestroy }                    from '@angular/core';
import { HttpErrorResponse }                                       from '@angular/common/http';
import { interval, Subscription }                                  from 'rxjs';
import { take }                                                    from 'rxjs/operators';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatError, MatFormField, MatLabel }                        from '@angular/material/form-field';
import { MatInput }                                                from '@angular/material/input';
import { MatProgressSpinner }                                      from '@angular/material/progress-spinner';
import { Router }                                                  from '@angular/router';

import { StringUtils }                     from '../../../../../shared/utils/string.utils';
import { PasswordCodeNotification }        from '../../../../../shared/data/password-code-notification';
import { PasswordTooltipDirective }        from '../../../../../shared/directives/password-tooltip.directive';
import { PasswordUtils }                   from '../../../../../shared/utils/password.utils';
import { FormUtils }                       from '../../../../../shared/utils/form.utils';
import { AuthenticationService }           from '../../../../../core/auth/authentication.service';
import { PinNotificationComponent }        from '../../../../../shared/components/notification/notification.component';
import { AlphanumericCodeDirective }       from '../../../../../shared/directives/alphanumeric-code.directive';
import { AutoFocusDirective }              from '../../../../../shared/directives/auto-focus.directive';
import { ButtonComponent, ButtonType }     from '../../../../../shared/components/buttons/button.component';
import { PasswordInputComponent }          from '../../../../../shared/components/password-input/password-input.component';
import { PasswordInfoComponent }           from '../../../../../shared/components/dialog/password-info/password-info.component';
import { DcpInternalErrorCode }            from '../../../../../core/error-handling/dcp-error-model';
import { ErrorService }                    from '../../../../../core/error-handling/error.service';
import { InternalRoutes }                  from '../../../../../shared/data/internal-routes';

@Component({
  selector: 'pin-create-password',
  templateUrl: './create-password.component.html',
  hostDirectives: [PasswordTooltipDirective],
  imports: [PinNotificationComponent, ReactiveFormsModule, MatFormField, MatLabel, MatInput, AlphanumericCodeDirective, AutoFocusDirective, MatError, ButtonComponent, PasswordInputComponent, PasswordInfoComponent, MatProgressSpinner]
})
export class CreatePasswordComponent implements OnDestroy {
  readonly passwordTooltip = inject(PasswordTooltipDirective);
  resendingCode = false;
  changingPassword = false;
  resendCountdown = 0;
  private readonly resendCountdownSeconds = 30;
  private resendCountdownTimer: Subscription;
  private resendTimeout: ReturnType<typeof setTimeout>;
  @Input() username: string;
  @Input() isPasswordlessSetup = false;
  passwordCodeNotification: PasswordCodeNotification;
  passwordCodeNotificationValues = PasswordCodeNotification;
  public verifyForm = new FormGroup(
    {
      code: new FormControl<string>('', [Validators.required]),
      password: new FormControl<string>('', [Validators.required, Validators.pattern(StringUtils.PASSWORD_VALIDATION_REGEX)]),
      passwordConfirmation: new FormControl<string>('', [Validators.required])
    },
    [PasswordUtils.samePasswordValidator]
  );
  private authService = inject(AuthenticationService);
  private errorService = inject(ErrorService);
  private router = inject(Router);

  handleEnterKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.resendValidationCode();
    }
  }

  onPasswordBlur() {
    this.passwordTooltip.onPasswordBlur();
    this.verifyForm.controls['passwordConfirmation'].updateValueAndValidity();
  }

  changePassword() {
    if (!this.verifyForm.valid) {
      FormUtils.markAllControlsTouchedAndValidate(this.verifyForm);
      return;
    }
    const code = this.verifyForm.getRawValue()['code'];
    const password = this.verifyForm.getRawValue()['password'];
    this.changingPassword = true;
    this.authService.recoverPassword(code, password, this.username)
      .subscribe({
        next: () => {
          this.changingPassword = false;
          this.router.navigate(['/login'], {queryParams: {forgotPasswordChange: true}}).then();
        },
        error: error => {
          this.handleError(error);
          this.changingPassword = false;
        }
      });
  }

  resendValidationCode() {
    if (this.resendingCode || this.resendCountdown > 0) {
      return;
    }
    this.resendingCode = true;
    this.startResendCountdown();
    this.authService.resendRecoveryCode(this.username)
      .subscribe({
        next: () => {
          this.passwordCodeNotification = this.passwordCodeNotificationValues.code_resent;
          this.resendTimeout = setTimeout(() => this.resendingCode = false, 2000);
        },
        error: error => {
          this.handleError(error);
          this.resendTimeout = setTimeout(() => this.resendingCode = false, 2000);
        }
      });
    window.scroll(0, 0);
  }

  private startResendCountdown() {
    this.resendCountdown = this.resendCountdownSeconds;
    this.resendCountdownTimer?.unsubscribe();
    this.resendCountdownTimer = interval(1000)
      .pipe(take(this.resendCountdownSeconds))
      .subscribe({
        next: elapsed => this.resendCountdown = this.resendCountdownSeconds - elapsed - 1,
        complete: () => this.resendCountdown = 0
      });
  }

  handleError(error: HttpErrorResponse) {
    if (this.errorService.isInfrastructureError(error)) {
      this.passwordCodeNotification = this.passwordCodeNotificationValues.try_again_later;
      window.scroll(0, 0);
      return;
    }

    const errorMessage = typeof error?.error === 'string' ? error.error : error?.error?.message;

    switch (errorMessage) {
      case DcpInternalErrorCode.ERR_USER_NOT_FOUND:
        this.passwordCodeNotification = this.passwordCodeNotificationValues.code_resent;
        break;
      case DcpInternalErrorCode.ERR_LIMIT_EXCEEDED:
      case DcpInternalErrorCode.ERR_PASSWORD_LOCKED_OUT:
        this.router.navigate(['/login'], {queryParams: {limitExceededException: true}}).then();
        break;
      case DcpInternalErrorCode.ERR_INVALID_VERIFICATION_CODE:
      case DcpInternalErrorCode.ERR_INVALID_PARAMETER:
      case DcpInternalErrorCode.ERR_REQUEST_VALIDATION_EXCEPTION:
        this.passwordCodeNotification = this.passwordCodeNotificationValues.code_incorrect;
        break;
      case DcpInternalErrorCode.ERR_EXPIRED_VERIFICATION_CODE:
        this.passwordCodeNotification = this.passwordCodeNotificationValues.code_expired;
        break;
      case DcpInternalErrorCode.ERR_PASSWORD_POLICY:
        this.passwordCodeNotification = undefined;
        this.verifyForm.controls['password'].setErrors({ pattern: true });
        this.verifyForm.controls['password'].markAsTouched();
        this.verifyForm.controls['password'].markAsDirty();
        break;
      case DcpInternalErrorCode.ERR_FLOW_EXPIRED:
        this.router.navigate(['/login'], {queryParams: {sessionExpired: true}}).then();
        break;
      default:
        this.router.navigate([InternalRoutes.ERROR]).then();
    }

    if (errorMessage !== DcpInternalErrorCode.ERR_PASSWORD_POLICY) {
      window.scroll(0, 0);
    }
  }

  ngOnDestroy() {
    clearTimeout(this.resendTimeout);
    this.resendCountdownTimer?.unsubscribe();
  }

  protected readonly buttonType = ButtonType;
}
